/**
 * Which rate years an offer actually prices.
 *
 * ⚠️ READ THE RAW OFFER DATA, NEVER THE MERGED SHAPE.
 *
 * createEmptyEnvironmentalCharges() hands back year_1 … year_6 for every scheme, and the deep
 * merge in getOfferEnvCharges walks the DEFAULTS' keys — so a year a reviewer deleted in the
 * parsing step comes back out of the merge as a fully-formed slot full of zeros. Combined with
 * a row count taken from ceil(termMonths / 12), that is what put an empty "Year 5" row on the
 * comparison screen after Year 5 had been deleted: the deletion did persist, but the renderer
 * asked the defaults how many years existed rather than asking the offer.
 */

/** A year slot counts as priced when any of its own numbers is a real, non-zero value. */
const slotIsPriced = (slot: unknown): boolean => {
    if (slot === null || slot === undefined) return false;
    if (typeof slot === 'number') return isFinite(slot) && slot !== 0;
    if (typeof slot !== 'object') return false;
    return Object.entries(slot as Record<string, unknown>).some(([key, value]) => {
        if (key === 'date_range' || key === 'dateRange') return false;
        const n = Number(value);
        return value !== null && value !== undefined && isFinite(n) && n !== 0;
    });
};

/** Year number out of a `year_N` key, or null. */
const yearNumberOf = (key: string): number | null => {
    const match = /^year_(\d+)$/.exec(key);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * The year numbers a single year map prices, ascending.
 *
 * Accepts both shapes the app stores: a map of year slots ({ year_1: {...} }) and a map of
 * plain numbers ({ year_1: 9.9076 }), which is how peakRates / offpeakRates arrive.
 */
export const pricedYearNumbers = (yearMap: unknown): number[] => {
    if (!yearMap || typeof yearMap !== 'object') return [];
    const years: number[] = [];
    for (const [key, value] of Object.entries(yearMap as Record<string, unknown>)) {
        const year = yearNumberOf(key);
        if (year !== null && slotIsPriced(value)) years.push(year);
    }
    return years.sort((a, b) => a - b);
};

/**
 * The highest year number anything in `source` prices — scanning one level of nesting so a
 * scheme-keyed environmental object ({ LREC: { year_1: … } }) and a bare year map both work.
 *
 * Returns 0 when nothing is priced, which callers read as "fall back to the term's year count".
 */
export const maxPricedYear = (source: unknown): number => {
    if (!source || typeof source !== 'object') return 0;
    let max = 0;
    for (const year of pricedYearNumbers(source)) max = Math.max(max, year);
    for (const value of Object.values(source as Record<string, unknown>)) {
        for (const year of pricedYearNumbers(value)) max = Math.max(max, year);
    }
    return max;
};
